(() => {
  const SELECTOR = "[data-delivery-timeframe]";
  const DAY_MS = 24 * 60 * 60 * 1000;

  function getTimezoneParts(timeZone) {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });

    const parts = {};

    for (const part of formatter.formatToParts(new Date())) {
      if (part.type !== "literal") {
        parts[part.type] = part.value;
      }
    }

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
    };
  }

  function createLogicalDate(year, month, day) {
    /*
     * UTC noon avoids browser timezone and daylight-saving changes
     * interfering with calendar-day calculations.
     */
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  function addCalendarDays(date, amount) {
    return new Date(date.getTime() + amount * DAY_MS);
  }

  function getMonthDay(date) {
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${month}-${day}`;
  }

  function isIncludedDate(date, includedWeekdays, blackoutDates) {
    const weekday = date.getUTCDay();
    const monthDay = getMonthDay(date);

    return (
      includedWeekdays.has(weekday) &&
      !blackoutDates.has(monthDay)
    );
  }

  function calculateDeliveryDate({
    baseDate,
    numberOfDays,
    startingOffset,
    includedWeekdays,
    blackoutDates,
  }) {
    let currentDate = addCalendarDays(baseDate, startingOffset);
    let countedDays = 0;
    let safetyCounter = 0;

    while (safetyCounter < 730) {
      if (isIncludedDate(currentDate, includedWeekdays, blackoutDates)) {
        countedDays += 1;

        if (countedDays >= numberOfDays) {
          return currentDate;
        }
      }

      currentDate = addCalendarDays(currentDate, 1);
      safetyCounter += 1;
    }

    throw new Error("Unable to calculate delivery date.");
  }

  function formatDate(date, locale) {
    return new Intl.DateTimeFormat(locale || "en", {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(date);
  }

  function formatDateRange(fromDate, toDate, locale) {
    const formatter = new Intl.DateTimeFormat(locale || "en", {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });

    if (
      typeof formatter.formatRange === "function" &&
      fromDate.getTime() !== toDate.getTime()
    ) {
      return formatter.formatRange(fromDate, toDate);
    }

    if (fromDate.getTime() === toDate.getTime()) {
      return formatter.format(fromDate);
    }

    return `${formatter.format(fromDate)} – ${formatter.format(toDate)}`;
  }

  function setElementValue(element, value) {
    if (element instanceof HTMLInputElement) {
      if (element.value !== value) {
        element.value = value;
      }

      return;
    }

    if (element.textContent.trim() !== value) {
      element.textContent = value;
    }
  }

  function renderPreorder(element) {
    const isoDate = element.dataset.preorderDate;
    const prefix = element.dataset.prefix || "Available from";
    const locale =
      element.dataset.locale ||
      document.documentElement.lang ||
      "en";

    if (!isoDate) return;

    const [year, month, day] = isoDate.split("-").map(Number);

    if (!year || !month || !day) return;

    const date = createLogicalDate(year, month, day);
    const value = `${prefix} ${formatDate(date, locale)}`;

    setElementValue(element, value);
  }

  function renderRange(element) {
    const minDays = Number(element.dataset.minDays);
    const maxDays = Number(element.dataset.maxDays);

    if (!Number.isFinite(minDays) || !Number.isFinite(maxDays)) {
      return;
    }

    const timeZone =
      element.dataset.timezone || "Europe/Amsterdam";

    const cutoffHour = Number(
      element.dataset.cutoffHour || 15
    );

    const includedWeekdays = new Set(
      String(element.dataset.includedWeekdays || "1,2,3,4,5,6")
        .split(",")
        .map(Number)
        .filter(Number.isFinite)
    );

    const blackoutDates = new Set(
      String(element.dataset.blackoutDates || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
    );

    const prefix =
      element.dataset.prefix || "Estimated delivery:";

    const locale =
      element.dataset.locale ||
      document.documentElement.lang ||
      "en";

    const timezoneParts = getTimezoneParts(timeZone);

    const baseDate = createLogicalDate(
      timezoneParts.year,
      timezoneParts.month,
      timezoneParts.day
    );

    /*
     * Before 15:00:
     * first possible counted date is tomorrow.
     *
     * At or after 15:00:
     * first possible counted date is the day after tomorrow.
     */
    const startingOffset =
      timezoneParts.hour >= cutoffHour ? 2 : 1;

    const earliestDate = calculateDeliveryDate({
      baseDate,
      numberOfDays: minDays,
      startingOffset,
      includedWeekdays,
      blackoutDates,
    });

    const latestDate = calculateDeliveryDate({
      baseDate,
      numberOfDays: maxDays,
      startingOffset,
      includedWeekdays,
      blackoutDates,
    });

    const range = formatDateRange(
      earliestDate,
      latestDate,
      locale
    );

    setElementValue(element, `${prefix} ${range}`);
  }

  function renderElement(element) {
    try {
      if (element.dataset.deliveryKind === "preorder") {
        renderPreorder(element);
        return;
      }

      renderRange(element);
    } catch (error) {
      console.error("Delivery timeframe error:", error);
    }
  }

  function renderAll(root = document) {
    if (root.matches?.(SELECTOR)) {
      renderElement(root);
    }

    root.querySelectorAll?.(SELECTOR).forEach(renderElement);
  }

  function initialize() {
    renderAll();

    let scheduled = false;

    const observer = new MutationObserver(() => {
      if (scheduled) return;

      scheduled = true;

      requestAnimationFrame(() => {
        renderAll();
        scheduled = false;
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    renderAll(event.target);
  });
})();